import { useEffect, useMemo, useState } from "react";
import api from "../services/api-service";
import { useAlert } from "../contexts/AlertContext";
import "../styles/analytics.css";

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Tooltip,
  Legend,
  Title,
} from "chart.js";
import { Bar, Doughnut, Line } from "react-chartjs-2";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Tooltip,
  Legend,
  Title,
);

export default function Analytics() {
  const { showAlert } = useAlert();

  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [byType, setByType] = useState([]);
  const [byStatus, setByStatus] = useState([]);
  const [byDay, setByDay] = useState([]);
  const [byLocation, setByLocation] = useState([]);

  const [range, setRange] = useState("30");
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        setLoading(true);
        setError("");

        const params = { days: range };

        const [summaryRes, typeRes, statusRes, dayRes, locationRes] =
          await Promise.all([
            api.get("/analytics/summary", { params }),
            api.get("/analytics/by-type", { params }),
            api.get("/analytics/by-status", { params }),
            api.get("/analytics/by-day", { params }),
            api.get("/analytics/by-location", { params }),
          ]);

        setSummary(summaryRes.data);
        setByType(typeRes.data || []);
        setByStatus(statusRes.data || []);
        setByDay(dayRes.data || []);
        setByLocation(locationRes.data || []);
      } catch (err) {
        console.error("Failed to fetch analytics", err);
        setError("Failed to load analytics.");
        showAlert("Failed to load analytics.", "error");
      } finally {
        setLoading(false);
      }
    };

    fetchAnalytics();
  }, [showAlert, range]);

  const incidentsByTypeData = useMemo(() => {
    return {
      labels: byType.map((item) => item.label ?? item.type ?? "Unknown"),
      datasets: [
        {
          label: "Incidents by type",
          data: byType.map((item) => item.count ?? 0),
          backgroundColor: [
            "rgba(249, 115, 22, 0.85)",
            "rgba(37, 99, 235, 0.85)",
            "rgba(34, 197, 94, 0.85)",
            "rgba(239, 68, 68, 0.85)",
            "rgba(168, 85, 247, 0.85)",
            "rgba(245, 158, 11, 0.85)",
          ],
          borderRadius: 8,
        },
      ],
    };
  }, [byType]);

  const incidentsByStatusData = useMemo(() => {
    return {
      labels: byStatus.map((item) => item.label ?? item.status ?? "Unknown"),
      datasets: [
        {
          label: "Incidents by status",
          data: byStatus.map((item) => item.count ?? 0),
          backgroundColor: [
            "rgba(34, 197, 94, 0.9)",
            "rgba(239, 68, 68, 0.9)",
            "rgba(245, 158, 11, 0.9)",
            "rgba(37, 99, 235, 0.9)",
          ],
          borderWidth: 0,
        },
      ],
    };
  }, [byStatus]);

  const incidentsByDayData = useMemo(() => {
    return {
      labels: byDay.map((item) => item.label ?? item.date ?? ""),
      datasets: [
        {
          label: "Incidents over time",
          data: byDay.map((item) => item.count ?? 0),
          borderColor: "rgba(249, 115, 22, 1)",
          backgroundColor: "rgba(249, 115, 22, 0.18)",
          tension: 0.35,
          fill: true,
          pointRadius: 4,
          pointHoverRadius: 6,
        },
      ],
    };
  }, [byDay]);

  const commonBarOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          precision: 0,
        },
      },
    },
  };

  const lineOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          precision: 0,
        },
      },
    },
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "bottom",
      },
    },
  };

  if (loading) {
    return <div className="analytics-loading">Loading analytics...</div>;
  }

  if (error) {
    return <div className="analytics-error">{error}</div>;
  }

  return (
    <div className="analytics-page">
      <div className="analytics-header">
        <div>
          <h2>Analytics</h2>
          <p>Overview of incidents by time, place and type.</p>
        </div>

        <div className="analytics-filters">
          <label htmlFor="range">Time range</label>
          <select
            id="range"
            value={range}
            onChange={(e) => setRange(e.target.value)}
          >
            <option value="7">Last 7 days</option>
            <option value="30">Last 30 days</option>
            <option value="90">Last 90 days</option>
            <option value="365">Last 365 days</option>
          </select>
        </div>
      </div>

      <div className="analytics-summary-grid">
        <div className="analytics-card summary-card">
          <span className="summary-label">Total incidents</span>
          <strong className="summary-value">
            {summary?.totalIncidents ?? 0}
          </strong>
        </div>

        <div className="analytics-card summary-card">
          <span className="summary-label">Approved</span>
          <strong className="summary-value success">
            {summary?.approvedCount ?? 0}
          </strong>
        </div>

        <div className="analytics-card summary-card">
          <span className="summary-label">Pending</span>
          <strong className="summary-value warning">
            {summary?.pendingCount ?? 0}
          </strong>
        </div>

        <div className="analytics-card summary-card">
          <span className="summary-label">Locations covered</span>
          <strong className="summary-value">
            {summary?.locationsCount ?? 0}
          </strong>
        </div>
      </div>

      <div className="analytics-grid">
        <div className="analytics-card chart-card large">
          <div className="chart-card-header">
            <h3>Incidents over time</h3>
          </div>
          <div className="chart-container line-chart-container">
            <Line data={incidentsByDayData} options={lineOptions} />
          </div>
        </div>

        <div className="analytics-card chart-card">
          <div className="chart-card-header">
            <h3>By status</h3>
          </div>
          <div className="chart-container doughnut-chart-container">
            <Doughnut data={incidentsByStatusData} options={doughnutOptions} />
          </div>
        </div>

        <div className="analytics-card chart-card">
          <div className="chart-card-header">
            <h3>By type</h3>
          </div>
          <div className="chart-container bar-chart-container">
            <Bar data={incidentsByTypeData} options={commonBarOptions} />
          </div>
        </div>

        <div className="analytics-card chart-card large">
          <div className="chart-card-header">
            <h3>Top locations</h3>
          </div>

          <div className="locations-table-wrapper">
            <table className="locations-table">
              <thead>
                <tr>
                  <th>Location</th>
                  <th>Incidents</th>
                </tr>
              </thead>
              <tbody>
                {byLocation.length === 0 ? (
                  <tr>
                    <td colSpan="2" className="empty-cell">
                      No location analytics available.
                    </td>
                  </tr>
                ) : (
                  byLocation.map((item, index) => (
                    <tr key={`${item.neighbourhood}-${index}`}>
                      <td>{item.neighbourhood ?? "Unknown"}</td>
                      <td>{item.count ?? 0}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
